import { useEffect, useRef, useState } from 'react'

import { useDeviceDrawerData } from '../hooks/useDeviceDrawerData'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useNavigation } from '@/hooks/useNavigation'
import { usePermissions } from '@/hooks/usePermissions'
import { BiDoorOpen } from 'react-icons/bi'
import { HiArrowLeft } from 'react-icons/hi'

import {
  Box,
  Circle,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  IconButton,
  SkeletonCircle,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react'
import { useInViewRef } from 'rooks'

import {
  DrawerBreadcrumb,
  DrawerTabs,
  EditPencilButton,
  ShowVideoButton,
} from '@/components/ui'
import { EditDeviceFieldsI } from '@/features/device-crud'
import { DrawerTabIProps, useDeviceDrawerTabs } from '@/features/device-drawer'
import { DeviceFeed } from '@/features/device-feed'
import {
  MSEVideoPlayer,
  VideoFeedEvent,
  VideoPlayer,
  useFindDeviceFeed,
} from '@/features/video-player'
import { DeviceType } from '@/graphql/generated/schemas'
import { AnalyticsScreen, useMixpanel } from '@/utils/analytics'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'
import { getDeviceStatusColor } from '@/utils/devices'

interface IProps {
  defaultIndex: number
  deviceId: string
  isOpen: boolean
  onClose: () => void
  onEditClick: (deviceData: EditDeviceFieldsI) => void
}

export const ACDeviceDrawer = ({
  defaultIndex,
  isOpen,
  onClose,
  deviceId,
  onEditClick,
}: IProps) => {
  const { goToFloorPlan } = useNavigation()
  const { shouldEnableUpdateDevice } = usePermissions()
  const { device, incidents, isIncidentsLoading, isLoading } =
    useDeviceDrawerData(deviceId)

  const [index, setIndex] = useState(defaultIndex)
  const [isPipActive, setPIPActive] = useState(false)
  const bodyRef = useRef<HTMLDivElement>()
  const [videoRef, isInView] = useInViewRef()

  // Feature flag for MSE-based authenticated proxy streaming
  const isMSEStreamingEnabled = useFeatureFlag(
    FeatureFlag.authenticatedProxyStreaming
  )

  const {
    createCameraEventHandler,
    createPlayerActionHandler,
    markStreamRequested,
  } = useMixpanel(AnalyticsScreen.DeviceDrawer)

  useEffect(() => {
    if (isOpen) {
      markStreamRequested()
    }
  }, [isOpen])

  const mixpanelData = () => {
    return {
      device_id: deviceId,
      facility_id: device?.facility?.id,
      facility_address: device?.facility?.address,
      floor_id: device?.floor?.id,
      floor_name: device?.floor?.name,
      streaming_type: device?.camera?.streamingType,
    }
  }
  const handleCameraEvent = createCameraEventHandler(mixpanelData())
  const handlePlayerAction = createPlayerActionHandler(mixpanelData())

  const {
    stream: { url: cameraFeedUrl, isLoading: isCameraFeedLoading },
    nextCamera,
    previousCamera,
    availableCamerasIds,
    currentCameraId,
    errorMessage,
  } = useFindDeviceFeed(device?.id, handleCameraEvent)

  useEffect(() => {
    setIndex(defaultIndex)
  }, [defaultIndex])

  const disablePIP = () => setPIPActive(false)
  const enablePIP = () => setPIPActive(true)

  const scrollToVideoPlayer = () => {
    bodyRef.current.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  const tabs: DrawerTabIProps[] = useDeviceDrawerTabs({
    incidents,
    isIncidentsLoading,
    deviceId,
  })

  const handleEditClick = () => {
    if (device) {
      trackEvent(`${BUTTON_PRESS} ACDeviceDrawer Edit Door`, mixpanelData())
      onEditClick({
        id: deviceId,
        floorId: device.floor?.id,
        name: device.name,
        type: DeviceType.Door,
      })
    }
  }

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement='right' size='lg'>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader borderBottom='1px solid #D5DCE4' p={0} pr='24px'>
          <Flex
            alignItems='center'
            h='64px'
            justifyContent='space-between'
            w='full'
          >
            <HStack isTruncated pl='24px' spacing={2}>
              <IconButton
                aria-label='Back button'
                borderRadius='md'
                h='30px'
                icon={<HiArrowLeft size={18} />}
                minWidth='30px'
                onClick={onClose}
                variant='ghost'
                w='30px'
              />

              {isLoading ? (
                <SkeletonCircle size='30px' />
              ) : (
                <Circle
                  bg={getDeviceStatusColor(device?.status)}
                  color='#fff'
                  size='30px'
                >
                  <BiDoorOpen size={16} />
                </Circle>
              )}
              <HStack isTruncated spacing='14px'>
                <DrawerBreadcrumb
                  isLoading={isLoading}
                  label={device?.name}
                  maxWidth='175px'
                />
                <DrawerBreadcrumb
                  isLoading={isLoading}
                  label={`Floor ${device?.floor?.name}`}
                  maxWidth='175px'
                  onClick={() => {
                    onClose()
                    goToFloorPlan(device?.floor?.id)
                  }}
                />
                <DrawerBreadcrumb
                  hideDivider={true}
                  isLoading={isLoading}
                  label={device?.facility?.shortName}
                  maxWidth='175px'
                  onClick={() => {
                    onClose()
                    goToFloorPlan(device?.floor?.id)
                  }}
                />
              </HStack>
            </HStack>
            <EditPencilButton
              disabledTooltipLabel='You do not have permissions to edit this device'
              isDisabled={!shouldEnableUpdateDevice}
              isLoading={isLoading}
              onClick={handleEditClick}
              tooltipLabel='Edit Door'
            />
          </Flex>
        </DrawerHeader>
        <DrawerBody p={0} ref={bodyRef}>
          <Box
            bg='white'
            position='sticky'
            ref={videoRef}
            top='-352px'
            zIndex='sticky'
          >
            <Box pt='24px' px='24px' w='100%'>
              <DeviceFeed
                isLoading={isCameraFeedLoading}
                onClickNext={nextCamera}
                onClickPrevious={previousCamera}
                showCameraSwitcher={availableCamerasIds.length > 1}
              >
                {isMSEStreamingEnabled && currentCameraId ? (
                  <MSEVideoPlayer
                    autoplay={true}
                    cameraId={currentCameraId}
                    errorLabel={errorMessage}
                    onEvent={handleCameraEvent}
                    onPlayerControlAction={handlePlayerAction}
                    onStartPlaying={() =>
                      handleCameraEvent(VideoFeedEvent.Playing)
                    }
                    showControls={true}
                  />
                ) : (
                  <VideoPlayer
                    disablePIP={disablePIP}
                    errorLabel={errorMessage}
                    onPlayerControlAction={handlePlayerAction}
                    onStartPlaying={() =>
                      handleCameraEvent(VideoFeedEvent.Playing)
                    }
                    pipActive={isPipActive}
                    showToggleLiveButton={false}
                    url={cameraFeedUrl}
                  />
                )}
              </DeviceFeed>
            </Box>
          </Box>
          <Tabs index={index} isLazy onChange={(i) => setIndex(i)}>
            <Box
              bg='white'
              boxShadow='lg'
              position='sticky'
              top='0'
              zIndex='sticky'
            >
              <ShowVideoButton
                disablePIP={disablePIP}
                enablePIP={enablePIP}
                onClick={scrollToVideoPlayer}
                pipActive={isPipActive}
                show={!isInView}
                url={cameraFeedUrl}
              />
              <DrawerTabs tabs={tabs} />
              <Box
                bgGradient='linear-gradient(135deg, #2E6CDB 0%, #6096F5 100%)'
                h='4px'
                w='full'
              />
            </Box>

            <TabPanels>
              {tabs.map(({ panel, label }) => (
                <TabPanel key={label} p='0'>
                  {panel}
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}
